/**
 * UAE-2 — AuthorEvidenceDialog.
 *
 * Opens when the user clicks `[Author]` on a Coverage Matrix
 * requirement row. Renders the per-requirement structured fields
 * declared on `ChecklistItem.authoredSchema` plus a universal
 * `notes` textarea + a confidence dropdown. On Save the form is
 * handed to `writeAuthoredClaim` which builds an `ExtractedClaim`
 * with `source: 'user-typed'`, `requirementId`, `authoredKind`,
 * and `structuredFields` and persists it through
 * `mergeClaimsIntoSiteContext`.
 *
 * Confidence is system-inferred by default (`inferAuthoredConfidence`)
 * — the user CAN override via the dropdown but the inference is the
 * starting value so they don't reflexively pick `high` and corrupt the
 * SED resolver (see UAE prompt-pack Risk R2).
 *
 * UX details:
 *   - Single column. Native form controls (the design system's
 *     base-ui Select wrapper is heavy for a 1-control dropdown).
 *   - Confidence label updates live as the user fills fields ("Default:
 *     medium (3/5 fields filled). Override:") so the inference is
 *     visible.
 *   - Save button is disabled until at least ONE input is populated
 *     (any structured field OR notes). Prevents accidental empty
 *     claims when the user opens the dialog and closes it without
 *     editing.
 *   - The dialog itself runs in plain JSX — no portal / animation
 *     dependencies — so it's easy to mount in vitest's jsdom
 *     environment.
 */
import React from 'react';
import { evidenceSchemaForStage, preserveHiddenEvidenceFields } from '../../../lib/coverage/stageEvidenceFields';
import { createPortal } from 'react-dom';
import { Sparkles, X as XIcon } from 'lucide-react';
import type { AuthoredFieldDef, AuthoredEvidenceFormState } from '../../../types/authoredEvidence';
import {
  inferAuthoredConfidence,
  isFieldFilled,
  makeEmptyForm,
} from '../../../types/authoredEvidence';
import type { ExtractedClaim } from '../../../types/siteContext';
import type { ChecklistItem } from '../../../config/documentChecklists';
import { writeAuthoredClaim } from '../../../services/siteContext/authoredEvidenceWriter';
import type { RequirementLineage } from '../../../lib/evidenceLineage/requirementLineage';
import { seedFromLineage, type PrefilledFieldOrigin } from '../../../lib/evidenceLineage/prefillFromLineage';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';
import type { SiteDocument } from '../../../types/siteRag';
import {
  resolveRequirementFields,
  formatFieldValue,
} from '../../../lib/evidenceLineage/requirementToFields';
import {
  evaluateSedFieldCompleteness,
  STRONG_STRUCTURED_FRACTION,
} from '../../../lib/coverage/evaluateRequirementCompleteness';
import { FieldProvenanceChip } from './coverageMatrixUi';
import { AuthoredFieldInput } from './AuthoredFieldInput';

export interface AuthorEvidenceDialogProps {
  /** Site this claim is being authored against. */
  siteId: string;
  /** Coverage Matrix requirement id (e.g. 'geotech'). */
  requirementId: string;
  /** Human-readable label rendered in the header. */
  requirementLabel: string;
  /** Per-requirement schema; empty array → prose-only. */
  schema: readonly AuthoredFieldDef[];
  /** Workflow-only field selection; persistence retains the full supplied schema. */
  decisionStage?: 1 | 2;
  /** Optional existing claim being edited — pre-fills the form. */
  existing?: ExtractedClaim;
  /**
   * EVL-2 — when supplied, the dialog seeds blank fields from the
   * matching SED slots so a "Strong" row no longer opens empty. Each
   * pre-filled field gets a "Pre-filled from {source}" badge and a
   * per-field `[Clear]` action. `existing` takes precedence — the
   * lineage seed only runs when there's no prior authored claim.
   */
  prefillFromLineage?: RequirementLineage;
  /** SED snapshot — drives the "Already on the SED" context block. */
  sed?: SiteEvidenceDocument;
  /** Current attached documents qualify source-bound SED completeness. */
  documents?: ReadonlyArray<SiteDocument>;
  /** Opens RequirementLineageInspector from the SED context block. */
  onOpenLineage?: (lineage: RequirementLineage) => void;
  /** Fired when the dialog closes (Cancel button OR successful Save). */
  onClose: () => void;
  /** Fired after a successful save. Caller can refresh the matrix. */
  onSaved?: (result: { claimId: string }) => void;
}

/** Seed `AuthoredEvidenceFormState` from an existing claim (edit mode). */
function seedFromExisting(
  existing: ExtractedClaim | undefined,
  schema: readonly AuthoredFieldDef[],
): AuthoredEvidenceFormState {
  const base = makeEmptyForm(schema);
  if (!existing) return base;
  for (const f of schema) {
    if (f.key === 'notes') continue;
    const v = existing.structuredFields?.[f.key];
    if (v !== undefined) base.fields[f.key] = v;
  }
  // Recover the `notes` content from the composed claim text — the
  // claim text is shaped as `<labels...>. Notes: <notes>`; we round-
  // trip the suffix when present so the user can edit it. If the
  // claim was prose-only the whole text becomes the notes seed.
  const source = existing.sourceSpan ?? existing.claim ?? '';
  const notesIdx = source.indexOf('Notes: ');
  if (notesIdx >= 0) {
    base.notes = source.slice(notesIdx + 'Notes: '.length).trim();
  } else if (existing.authoredKind === 'prose') {
    base.notes = source.trim();
  }
  // Map the persisted score back to a band.
  const score = existing.extractorConfidence ?? 0;
  if (score >= 0.75) base.confidence = 'high';
  else if (score >= 0.6) base.confidence = 'medium';
  else base.confidence = 'low';
  return base;
}

export const AuthorEvidenceDialog: React.FC<AuthorEvidenceDialogProps> = ({
  siteId,
  requirementId,
  requirementLabel,
  schema: fullSchema,
  decisionStage,
  existing,
  prefillFromLineage,
  sed,
  documents,
  onOpenLineage,
  onClose,
  onSaved,
}) => {
  const schema = React.useMemo(() => evidenceSchemaForStage(requirementId, fullSchema, decisionStage), [requirementId, fullSchema, decisionStage]);
  // EVL-2 — seed state. Precedence: existing-claim > lineage > empty.
  // Lineage prefill runs ONCE on dialog mount (the `useState` initializer
  // captures the snapshot); subsequent prop changes do NOT re-seed so
  // the user's in-progress edits aren't clobbered (Risk R2 mitigation).
  const seed = React.useState(() => {
    if (existing) {
      return {
        form: preserveHiddenEvidenceFields(seedFromExisting(existing, schema), existing, fullSchema, schema),
        prefilledFrom: {} as Record<string, PrefilledFieldOrigin>,
      };
    }
    if (prefillFromLineage) {
      const r = seedFromLineage(prefillFromLineage, schema);
      return { form: r.form, prefilledFrom: r.prefilledFrom };
    }
    return {
      form: makeEmptyForm(schema),
      prefilledFrom: {} as Record<string, PrefilledFieldOrigin>,
    };
  })[0];
  const [form, setForm] = React.useState<AuthoredEvidenceFormState>(seed.form);
  // EVL-2 — track which fields still carry the lineage seed. Cleared
  // on user edit so the badge only shows for unmodified pre-filled
  // values. The set is keyed by `AuthoredFieldDef.key`.
  const [prefilledFrom, setPrefilledFrom] = React.useState<Record<string, PrefilledFieldOrigin>>(
    seed.prefilledFrom,
  );
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  /** Set only when the user explicitly picks a confidence band. */
  const [confidenceOverride, setConfidenceOverride] = React.useState<
    'low' | 'medium' | 'high' | null
  >(null);

  const inferred = React.useMemo(
    () => inferAuthoredConfidence(form, schema),
    [form, schema],
  );
  const effectiveConfidence = confidenceOverride ?? inferred;

  const filledFieldCount = React.useMemo(
    () => schema.filter(f => f.key !== 'notes' && isFieldFilled(form.fields[f.key])).length,
    [form.fields, schema],
  );
  const totalStructuredCount = schema.filter(f => f.key !== 'notes').length;

  const canSave =
    !saving &&
    (filledFieldCount > 0 || isFieldFilled(form.notes));

  const hasDraft = canSave;

  const setFieldValue = React.useCallback(
    (key: string, value: string | number | boolean) => {
      setForm(f => ({ ...f, fields: { ...f.fields, [key]: value } }));
      // EVL-2 — user edited a pre-filled field; drop the badge so the
      // save path stamps `'user-edited'` rather than `'prefilled'`.
      setPrefilledFrom(prev => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [],
  );

  // EVL-2 — explicit clear action on a pre-filled field. Empties the
  // value AND drops the badge so the user can knowingly override
  // with a blank.
  const clearField = React.useCallback(
    (key: string) => {
      const schemaField = schema.find(f => f.key === key);
      const empty: string | number | boolean = schemaField?.type === 'boolean' ? false : '';
      setForm(f => ({ ...f, fields: { ...f.fields, [key]: empty } }));
      setPrefilledFrom(prev => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [schema],
  );

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const result = await writeAuthoredClaim({
        siteId,
        requirementId,
        requirementLabel,
        schema: fullSchema,
        form: { ...form, confidence: effectiveConfidence },
      });
      onSaved?.({ claimId: result.claimId });
      onClose();
    } catch (e) {
      setError((e as Error)?.message ?? 'Failed to save authored evidence');
      setSaving(false);
    }
  };

  // Filter out notes from the structured field list — notes is rendered
  // separately as the dedicated textarea below.
  const structuredFields = schema.filter(f => f.key !== 'notes');
  const notesField = schema.find(f => f.key === 'notes');

  const sedContextFields = React.useMemo(() => {
    if (!prefillFromLineage || !sed) return [];
    return resolveRequirementFields(sed, requirementId, true)
      .filter(f => !f.isEmpty)
      .slice(0, 5);
  }, [prefillFromLineage, sed, requirementId]);

  const sedCompleteness = React.useMemo(() => {
    if (!prefillFromLineage) return null;
    const requirementStub: ChecklistItem = {
      id: requirementId,
      label: requirementLabel,
      description: '',
      sedSectionsHelpful: [],
      relevanceTagPattern: [],
      severity: 'recommended',
      authoredSchema: schema,
    };
    return evaluateSedFieldCompleteness(requirementId, sed, { requirement: requirementStub, documents });
  }, [prefillFromLineage, requirementId, requirementLabel, schema, sed, documents]);

  const strongFieldThreshold = sedCompleteness?.structuredTotal
    ? Math.ceil(sedCompleteness.structuredTotal * STRONG_STRUCTURED_FRACTION)
    : 0;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="author-evidence-dialog-title"
      data-testid="author-evidence-dialog"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-background/80 p-4"
      onMouseDown={(e) => {
        // Dismiss on backdrop mousedown only — not click. A click that
        // starts inside an input and ends on the backdrop (common when
        // selecting text or dragging) must not close the dialog.
        if (e.target !== e.currentTarget) return;
        if (hasDraft) return;
        onClose();
      }}
    >
      <div
        className="w-full max-w-3xl bg-popover text-popover-foreground border border-border rounded-xl shadow-xl flex flex-col md:flex-row max-h-[90vh] overflow-hidden"
        data-testid="author-evidence-dialog-panel"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {prefillFromLineage && sed && (
          <aside
            className="md:w-52 shrink-0 border-b md:border-b-0 md:border-r border-border-subtle bg-surface-1/50 p-3 space-y-2 overflow-y-auto"
            data-testid="author-evidence-sed-context"
          >
            <h3 className="type-section text-text-secondary">
              Already on the SED
            </h3>
            {sedContextFields.length > 0 ? (
              <ul className="space-y-1.5">
                {sedContextFields.map(f => (
                  <li key={f.path} className="text-[10px]">
                    <div className="text-text-muted">{f.label}</div>
                    <div className="font-mono text-text-primary truncate">{formatFieldValue(f.value)}</div>
                    <FieldProvenanceChip provenance={f.provenance} sourceLabel={f.sourceLabel} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[10px] text-text-muted italic">No populated fields yet.</p>
            )}
            {sedCompleteness && sedCompleteness.structuredTotal > 0 && (
              <p className="type-body text-text-muted">
                {sedCompleteness.structuredFilled < strongFieldThreshold
                  ? `Fill ${strongFieldThreshold - sedCompleteness.structuredFilled} more field${strongFieldThreshold - sedCompleteness.structuredFilled === 1 ? '' : 's'} to reach Strong.`
                  : 'Field completeness meets the Strong bar.'}
              </p>
            )}
            {onOpenLineage && (
              <button
                type="button"
                onClick={() => onOpenLineage(prefillFromLineage)}
                className="text-[10px] font-semibold text-primary hover:underline"
                data-testid="author-evidence-open-lineage"
              >
                Open evidence chain
              </button>
            )}
          </aside>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
        <header className="px-4 py-3 border-b border-border-subtle">
          <h2
            id="author-evidence-dialog-title"
            className="text-sm font-bold uppercase tracking-widest text-text-primary"
          >
            Author evidence
          </h2>
          <p className="text-[10px] text-text-muted mt-0.5">{requirementLabel}</p>
        </header>

        <div
          className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
          data-testid="author-evidence-dialog-body"
        >
          {/* EVL-2 — summary banner when the dialog was opened against
              a Strong row with no prior authored claim. Tells the user
              "here's what we already know from the SED" so they don't
              re-type. */}
          {!existing && Object.keys(prefilledFrom).length > 0 && (
            <div
              className="border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1.5 text-[10px] text-text-primary flex items-start gap-1.5"
              data-testid="author-evidence-prefill-banner"
            >
              <Sparkles className="w-3 h-3 text-emerald-600 mt-0.5 shrink-0" aria-hidden="true" />
              <div>
                <div className="font-bold uppercase tracking-widest type-body text-emerald-700">
                  Pre-filled from existing evidence
                </div>
                <div className="text-text-secondary mt-0.5">
                  {Object.keys(prefilledFrom).length} field{Object.keys(prefilledFrom).length === 1 ? '' : 's'} populated from
                  {' '}
                  {Array.from(new Set(Object.values(prefilledFrom).map(p => p.source))).join(', ')}.
                  Edit any value to override; click the badge × to clear.
                </div>
              </div>
            </div>
          )}

          {structuredFields.length === 0 && (
            <p className="text-[10px] text-text-muted italic">
              No structured schema for this requirement — use the notes below to record what you know.
            </p>
          )}

          {structuredFields.map(field => (
            <div key={field.key}>
              <AuthoredFieldInput
                field={field}
                value={form.fields[field.key]}
                onChange={(v) => setFieldValue(field.key, v)}
              />
              {prefilledFrom[field.key] && (
                <PrefilledBadge
                  fieldKey={field.key}
                  origin={prefilledFrom[field.key]}
                  onClear={() => clearField(field.key)}
                />
              )}
            </div>
          ))}

          {/* Universal notes textarea (or schema-defined notes if present). */}
          <div className="space-y-1">
            <label
              htmlFor="author-evidence-notes"
              className="type-section text-text-secondary"
            >
              {notesField?.label ?? 'Notes'}
            </label>
            {notesField?.help && (
              <p className="type-body text-text-muted">{notesField.help}</p>
            )}
            <textarea
              id="author-evidence-notes"
              data-testid="author-evidence-notes"
              className="w-full min-h-[80px] text-[11px] border border-border bg-background px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder={notesField?.placeholder ?? 'Optional free-text context.'}
              value={form.notes}
              onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
            />
          </div>

          {/* Confidence band — system-inferred default + override. */}
          <div className="space-y-1 pt-1 border-t border-border-subtle">
            <label
              htmlFor="author-evidence-confidence"
              className="type-section text-text-secondary"
            >
              Confidence
            </label>
            <p className="type-body text-text-muted" data-testid="author-evidence-confidence-hint">
              Default: <span className="font-bold">{inferred}</span>
              {totalStructuredCount > 0
                ? ` (${filledFieldCount}/${totalStructuredCount} fields filled).`
                : ' (prose only).'}
              {' Override:'}
            </p>
            <select
              id="author-evidence-confidence"
              data-testid="author-evidence-confidence"
              className="w-full text-[11px] border border-border bg-background px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
              value={effectiveConfidence}
              onChange={(e) => {
                const band = e.target.value as 'low' | 'medium' | 'high';
                setConfidenceOverride(band);
                setForm(f => ({ ...f, confidence: band }));
              }}
            >
              <option value="low">low (0.45)</option>
              <option value="medium">medium (0.60)</option>
              <option value="high">high (0.75)</option>
            </select>
            {effectiveConfidence === 'high' && inferred !== 'high' && (
              <p
                className="type-body text-warning rounded-md border border-warning/40 bg-warning/10 px-2 py-1"
                data-testid="author-evidence-confidence-override-warning"
              >
                High confidence requires all required fields and ≥80% of fields filled.
                The Coverage Matrix will not read Strong until that bar is met.
              </p>
            )}
          </div>

          {error && (
            <p
              className="text-[10px] text-danger rounded-md border border-danger/40 bg-danger/10 px-2 py-1"
              data-testid="author-evidence-error"
            >
              {error}
            </p>
          )}
        </div>

        <footer className="px-4 py-3 border-t border-border-subtle flex items-center justify-end gap-2">
          <button
            type="button"
            data-testid="author-evidence-cancel"
            onClick={onClose}
            disabled={saving}
            className="text-[11px] uppercase tracking-widest text-text-secondary hover:underline disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="author-evidence-save"
            onClick={handleSave}
            disabled={!canSave}
            className="text-[11px] uppercase tracking-widest bg-primary text-primary-foreground px-3 py-1.5 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </footer>
        </div>
      </div>
    </div>,
    document.body,
  );
};

/**
 * EVL-2 — small badge rendered next to a pre-filled control. Shows
 * the source label + bucketed confidence (low/moderate/high — Risk
 * R4 mitigation: never displays a misleadingly-precise percent) and
 * offers a per-field clear action.
 */
function confidenceBucket(score: number): { label: string; tone: string } {
  if (score >= 0.75) return { label: 'high', tone: 'text-emerald-700' };
  if (score >= 0.55) return { label: 'moderate', tone: 'text-emerald-700' };
  return { label: 'low', tone: 'text-amber-700' };
}

const PrefilledBadge: React.FC<{
  fieldKey: string;
  origin: PrefilledFieldOrigin;
  onClear: () => void;
}> = ({ fieldKey, origin, onClear }) => {
  const conf = confidenceBucket(origin.confidence);
  return (
    <div
      className="type-body text-emerald-700 flex items-center gap-1 mt-1"
      data-testid={`author-evidence-prefilled-${fieldKey}`}
      title={`From ${origin.sectionId} · ${Math.round(origin.confidence * 100)}% raw confidence (${conf.label}).`}
    >
      <Sparkles className="w-2.5 h-2.5" aria-hidden="true" />
      <span>
        Pre-filled from <span className="font-bold">{origin.source}</span>
        {' · '}
        <span className={conf.tone}>{conf.label} confidence</span>
      </span>
      <button
        type="button"
        onClick={onClear}
        data-testid={`author-evidence-prefilled-clear-${fieldKey}`}
        className="ml-1 inline-flex items-center text-text-muted hover:text-text-primary"
        aria-label={`Clear pre-filled value for ${fieldKey}`}
      >
        <XIcon className="w-2.5 h-2.5" />
      </button>
    </div>
  );
};
